import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Sparkles, RefreshCw, Loader2, Users, Mail, Target, Clock, CheckCircle, XCircle, Wrench, Database } from 'lucide-react';

const fmtFecha = (s) => {
  if (!s) return '—';
  try {
    const d = new Date(s);
    return d.toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch (_) { return s; }
};

const GlobalStatCard = ({ label, value, sub, icon: Icon, color = 'brand' }) => (
  <Card className="p-4">
    <div className="flex items-start gap-3">
      <div className={`w-10 h-10 rounded-lg bg-${color}-50 text-${color}-700 flex items-center justify-center shrink-0`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-2xl font-heading font-bold text-ink-900">{value}</div>
        <div className="text-xs text-ink-500 uppercase tracking-wider">{label}</div>
        {sub && <div className="text-[11px] text-ink-400 mt-0.5">{sub}</div>}
      </div>
    </div>
  </Card>
);

const JobCard = ({ job_id, info, running }) => {
  const activo = info?.activo;
  return (
    <Card className="p-4" data-testid={`ia-stats-job-${job_id}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className="font-semibold text-sm text-ink-900">{job_id}</div>
        {info?.handler_placeholder ? (
          <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[9px] border">
            <Wrench className="w-2.5 h-2.5 mr-0.5 inline" /> placeholder
          </Badge>
        ) : (
          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[9px] border">real</Badge>
        )}
        {activo ? (
          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[9px] border">
            <CheckCircle className="w-2.5 h-2.5 mr-0.5 inline" /> activo
          </Badge>
        ) : (
          <Badge className="bg-ink-100 text-ink-600 border-ink-200 text-[9px] border">
            <XCircle className="w-2.5 h-2.5 mr-0.5 inline" /> inactivo
          </Badge>
        )}
      </div>
      <div className="text-xs text-ink-600 space-y-0.5">
        <div><span className="text-ink-500">Trigger:</span> cada {info?.trigger?.minutes || '—'} min</div>
        <div><span className="text-ink-500">Última ejecución:</span> {fmtFecha(info?.ultima_ejecucion)}</div>
        <div><span className="text-ink-500">Próxima ejecución:</span> {running ? fmtFecha(info?.proxima_ejecucion) : <em className="text-amber-700">scheduler detenido</em>}</div>
      </div>
    </Card>
  );
};

export default function IAStats() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const cargar = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/ia-stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || `HTTP ${r.status}`);
      }
      setData(await r.json());
    } catch (e) {
      toast.error(`Error: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const g = data?.global || {};
  const users = data?.users || [];

  return (
    <div className="max-w-7xl mx-auto p-6" data-testid="ia-stats-page">
      <div className="flex items-center justify-between mb-5 gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold text-ink-900 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-violet-600" /> Métricas IA Correo
          </h1>
          <p className="text-sm text-ink-500">Estado del indexado vectorial, detección de oportunidades y costes aproximados por usuario.</p>
        </div>
        <Button size="sm" variant="outline" onClick={cargar} disabled={loading} data-testid="ia-stats-recargar">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </Button>
      </div>

      {loading && !data && (
        <div className="py-20 text-center"><Loader2 className="w-8 h-8 animate-spin text-violet-600 mx-auto mb-2" /><p className="text-sm text-ink-500">Cargando…</p></div>
      )}

      {data && (
        <>
          {/* Global stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="ia-stats-global">
            <GlobalStatCard label="Users con correo" value={g.users_con_cuentas || 0} icon={Users} color="brand" />
            <GlobalStatCard label="Correos totales" value={g.correos_total || 0} icon={Mail} color="brand" />
            <GlobalStatCard label="Indexados" value={`${g.pct_embedded || 0}%`} sub={`${g.correos_embedded || 0} correos`} icon={Database} color="violet" />
            <GlobalStatCard label="Chunks vectorial" value={g.chunks_indexados || 0} icon={Sparkles} color="violet" />
            <GlobalStatCard label="🎯 Oportunidades 7d" value={g.oportunidades_semana || 0} icon={Target} color="amber" />
          </div>

          {/* Estado scheduler */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-ink-600" />
              <h2 className="text-sm font-semibold text-ink-800 uppercase tracking-wider">Cron jobs IA Correo</h2>
              {g.scheduler_running ? (
                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[9px] border">scheduler activo</Badge>
              ) : (
                <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[9px] border">scheduler detenido</Badge>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="ia-stats-jobs">
              <JobCard job_id="embeddings_correos" info={g.jobs?.embeddings_correos} running={g.scheduler_running} />
              <JobCard job_id="detectar_oportunidades" info={g.jobs?.detectar_oportunidades} running={g.scheduler_running} />
            </div>
          </div>

          {/* Tabla por usuario */}
          <Card className="overflow-x-auto" data-testid="ia-stats-tabla">
            <div className="flex items-center justify-between px-4 py-3 border-b border-ink-200 bg-ink-50">
              <h2 className="text-sm font-semibold text-ink-800">Usuarios con cuentas de correo</h2>
              <span className="text-xs text-ink-500">{users.length} users</span>
            </div>
            {users.length === 0 ? (
              <div className="py-12 text-center text-sm text-ink-500" data-testid="ia-stats-vacio">
                No hay usuarios con cuentas de correo conectadas todavía.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-ink-50 border-b border-ink-200">
                  <tr>
                    <th className="text-left px-3 py-2 text-xs uppercase tracking-wider text-ink-500">Usuario</th>
                    <th className="text-left px-3 py-2 text-xs uppercase tracking-wider text-ink-500">Rol</th>
                    <th className="text-right px-3 py-2 text-xs uppercase tracking-wider text-ink-500">Cuentas</th>
                    <th className="text-right px-3 py-2 text-xs uppercase tracking-wider text-ink-500">Correos</th>
                    <th className="text-right px-3 py-2 text-xs uppercase tracking-wider text-ink-500">% embed</th>
                    <th className="text-right px-3 py-2 text-xs uppercase tracking-wider text-ink-500">Chunks</th>
                    <th className="text-right px-3 py-2 text-xs uppercase tracking-wider text-ink-500">🎯 7d / total</th>
                    <th className="text-left px-3 py-2 text-xs uppercase tracking-wider text-ink-500">Último embed</th>
                    <th className="text-left px-3 py-2 text-xs uppercase tracking-wider text-ink-500">Toggles</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.user_id} className="border-b border-ink-100 hover:bg-ink-50/40" data-testid={`ia-stats-row-${u.user_id}`}>
                      <td className="px-3 py-2">
                        <div className="font-medium text-ink-900">{u.nombre}</div>
                        <div className="text-[10px] text-ink-500">{u.email}</div>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <Badge className="bg-ink-100 text-ink-700 border-0 text-[10px]">{u.rol}</Badge>
                      </td>
                      <td className="px-3 py-2 text-right text-xs">
                        {u.cuentas_correo} <span className="text-ink-400">({(u.cuentas_tipos || []).join(',')})</span>
                      </td>
                      <td className="px-3 py-2 text-right text-xs text-ink-700">{u.correos_total}</td>
                      <td className="px-3 py-2 text-right text-xs">
                        <span className={`font-medium ${u.pct_embedded >= 80 ? 'text-emerald-600' : u.pct_embedded >= 30 ? 'text-amber-600' : 'text-ink-400'}`}>
                          {u.pct_embedded}%
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right text-xs text-violet-700 font-medium">{u.chunks_indexados}</td>
                      <td className="px-3 py-2 text-right text-xs">
                        <span className="text-amber-700 font-medium">{u.oportunidades_semana}</span>
                        <span className="text-ink-400"> / {u.oportunidades_total}</span>
                      </td>
                      <td className="px-3 py-2 text-xs text-ink-500">{fmtFecha(u.ultimo_embed_at)}</td>
                      <td className="px-3 py-2 text-xs">
                        <span className={`mr-1 ${u.prefs?.deteccion_oportunidades_activa ? 'text-emerald-600' : 'text-ink-300'}`} title="Detección oportunidades">🎯</span>
                        <span className={`${u.prefs?.memorias_relevantes_activa ? 'text-blue-600' : 'text-ink-300'}`} title="Memorias relevantes">🧠</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          <p className="text-[11px] text-ink-400 mt-4">
            Los datos de chunks se leen directamente de chromadb. El % embed se calcula sobre la colección <code className="font-mono bg-ink-100 px-1 rounded">correos_sincronizados</code> del user. Si el scheduler está detenido, los cron jobs no se ejecutan — activa <code className="font-mono bg-ink-100 px-1 rounded">SCHEDULER_ENABLED=true</code> en producción.
          </p>
        </>
      )}
    </div>
  );
}
